/**
 * Mock API for local frontend development.
 *
 * Enabled when `VITE_USE_MOCK=true` (the default in `.env.development`) or when
 * running tests. Hand-crafted snapshots for SPX + a basket of peers so the UI
 * has realistic shape to render.
 *
 * v0.9.6 (Plan 9): mock data is now DETERMINISTIC per ticker — every ticker
 * has a distinct GEX shape, regime, and HIRO-style intraday walk so the UI
 * looks varied and real instead of "white noise everywhere." See
 * `docs/superpowers/specs/2026-05-02-plan9-design-intent.md` move #9.
 */

import type {
  GexBucket,
  GexProfile,
  HistoryResponse,
  Levels,
  LiveSnapshot,
  Methodology,
  MethodologyWhitepaper,
  PriorityAlert,
  PriorityAlertsResponse,
  RelayAlertsResponse,
  Regime,
  Ticker,
  UserProfile,
  WsMessage,
} from "./types";
import type { WsClient, WsHandler, WsState } from "./ws";

export function isMockEnabled(): boolean {
  if (typeof import.meta !== "undefined" && import.meta.env) {
    return String(import.meta.env.VITE_USE_MOCK).toLowerCase() === "true";
  }
  return false;
}

// ---------------------------------------------------------------------------
// Per-ticker mock profiles. Each profile defines a distinct visual character
// so the dashboard doesn't render 13 identical heatmaps.
// ---------------------------------------------------------------------------

interface TickerProfile {
  symbol: Ticker;
  spot: number;
  /** Per-strike step size relative to spot. */
  strikeStep: number;
  /** Number of strikes (rows) to generate. Should be odd so spot sits at center. */
  strikeCount: number;
  /** Where the gamma flip sits relative to spot (negative = below). */
  gammaFlipOffsetPct: number;
  /**
   * GEX shape generator. Returns signed GEX per strike given (offset_in_strikes,
   * total_strikes). Offsets are in [-half, +half] so spot ≈ 0.
   *
   * Different shapes:
   *   "concentrated_positive" — heavy positive cluster around spot (SPX, MSFT)
   *   "negative_skew" — negative below spot, positive above (QQQ, NDX in selloffs)
   *   "bimodal_call_heavy" — two positive humps above spot (NVDA, TSLA — retail call buying)
   *   "balanced" — symmetric mild positive (AAPL, GOOGL)
   *   "negative_dominant" — negative across most strikes (IWM, VIX)
   *   "wing_amplifying" — small at center, large at wings (META, AMZN — vol selling)
   */
  shape:
    | "concentrated_positive"
    | "negative_skew"
    | "bimodal_call_heavy"
    | "balanced"
    | "negative_dominant"
    | "wing_amplifying";
  /** Peak GEX magnitude in dollars per 1% move. */
  peakMagnitude: number;
  /** Intraday HIRO walk seed offset (deterministic per ticker). */
  walkSeed: number;
  /** Intraday change percentage (fraction). */
  changePct: number;
  /** Forced regime override (defaults to deriving from total). */
  regime?: Regime;
}

const TICKER_PROFILES: TickerProfile[] = [
  {
    symbol: "SPX",
    spot: 5847.32,
    strikeStep: 5,
    strikeCount: 41,
    gammaFlipOffsetPct: -0.0025,
    shape: "concentrated_positive",
    peakMagnitude: 2.4e9,
    walkSeed: 17,
    changePct: 0.0042,
    regime: "positive_gamma",
  },
  {
    symbol: "SPY",
    spot: 583.41,
    strikeStep: 0.5,
    strikeCount: 41,
    gammaFlipOffsetPct: -0.004,
    shape: "concentrated_positive",
    peakMagnitude: 1.6e9,
    walkSeed: 23,
    changePct: 0.0038,
    regime: "positive_gamma",
  },
  {
    symbol: "QQQ",
    spot: 502.18,
    strikeStep: 0.5,
    strikeCount: 41,
    gammaFlipOffsetPct: 0.005,
    shape: "negative_skew",
    peakMagnitude: 1.1e9,
    walkSeed: 41,
    changePct: -0.0019,
    regime: "negative_gamma",
  },
  {
    symbol: "NDX",
    spot: 20612.55,
    strikeStep: 25,
    strikeCount: 41,
    gammaFlipOffsetPct: 0.003,
    shape: "negative_skew",
    peakMagnitude: 8.5e8,
    walkSeed: 53,
    changePct: -0.0023,
    regime: "negative_gamma",
  },
  {
    symbol: "NVDA",
    spot: 138.42,
    strikeStep: 0.5,
    strikeCount: 41,
    gammaFlipOffsetPct: -0.012,
    shape: "bimodal_call_heavy",
    peakMagnitude: 6.2e8,
    walkSeed: 71,
    changePct: 0.0091,
    regime: "positive_gamma",
  },
  {
    symbol: "TSLA",
    spot: 252.18,
    strikeStep: 1,
    strikeCount: 41,
    gammaFlipOffsetPct: -0.018,
    shape: "bimodal_call_heavy",
    peakMagnitude: 5.4e8,
    walkSeed: 79,
    changePct: -0.0067,
    regime: "negative_gamma",
  },
  {
    symbol: "AAPL",
    spot: 232.41,
    strikeStep: 1,
    strikeCount: 41,
    gammaFlipOffsetPct: -0.005,
    shape: "balanced",
    peakMagnitude: 7.8e8,
    walkSeed: 89,
    changePct: 0.0014,
    regime: "positive_gamma",
  },
  {
    symbol: "MSFT",
    spot: 421.66,
    strikeStep: 1,
    strikeCount: 41,
    gammaFlipOffsetPct: -0.006,
    shape: "concentrated_positive",
    peakMagnitude: 9.3e8,
    walkSeed: 97,
    changePct: 0.0028,
    regime: "positive_gamma",
  },
  {
    symbol: "META",
    spot: 622.99,
    strikeStep: 2.5,
    strikeCount: 41,
    gammaFlipOffsetPct: -0.008,
    shape: "wing_amplifying",
    peakMagnitude: 4.7e8,
    walkSeed: 103,
    changePct: 0.0051,
    regime: "positive_gamma",
  },
  {
    symbol: "AMZN",
    spot: 222.10,
    strikeStep: 1,
    strikeCount: 41,
    gammaFlipOffsetPct: -0.004,
    shape: "wing_amplifying",
    peakMagnitude: 5.1e8,
    walkSeed: 113,
    changePct: 0.0017,
    regime: "positive_gamma",
  },
  {
    symbol: "GOOGL",
    spot: 195.30,
    strikeStep: 1,
    strikeCount: 41,
    gammaFlipOffsetPct: -0.007,
    shape: "balanced",
    peakMagnitude: 6.4e8,
    walkSeed: 127,
    changePct: -0.0027,
    regime: "neutral",
  },
  {
    symbol: "IWM",
    spot: 230.55,
    strikeStep: 0.5,
    strikeCount: 41,
    gammaFlipOffsetPct: 0.011,
    shape: "negative_dominant",
    peakMagnitude: 3.2e8,
    walkSeed: 131,
    changePct: -0.0044,
    regime: "negative_gamma",
  },
  {
    symbol: "VIX",
    spot: 14.32,
    strikeStep: 0.5,
    strikeCount: 41,
    gammaFlipOffsetPct: 0.047,
    shape: "negative_dominant",
    peakMagnitude: 1.8e8,
    walkSeed: 137,
    changePct: 0.026,
    regime: "negative_gamma",
  },
];

// ---------------------------------------------------------------------------
// Deterministic noise — Mulberry32 PRNG so every ticker gets stable but unique
// shapes. Stable shapes = the user can recognize a pattern between renders.
// ---------------------------------------------------------------------------

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** GEX magnitude shape function indexed by `TickerProfile.shape`. */
function gexShape(
  shape: TickerProfile["shape"],
  offset: number,
  half: number,
  rng: () => number,
): number {
  const x = offset / half; // x in [-1, 1]
  const noise = (rng() - 0.5) * 0.18;
  switch (shape) {
    case "concentrated_positive": {
      // Tall positive Gaussian centered at spot, very small negative tail below.
      const peak = Math.exp(-Math.pow(x / 0.18, 2));
      const tail = x < -0.3 ? -0.18 * Math.exp(-Math.pow((x + 0.6) / 0.25, 2)) : 0;
      return Math.max(-0.3, peak + tail + noise * 0.3);
    }
    case "negative_skew": {
      // Negative below spot, positive above.
      const negSide = x < 0 ? -Math.exp(-Math.pow((x + 0.3) / 0.25, 2)) * 0.95 : 0;
      const posSide = x > 0 ? Math.exp(-Math.pow((x - 0.4) / 0.3, 2)) * 0.6 : 0;
      return negSide + posSide + noise * 0.25;
    }
    case "bimodal_call_heavy": {
      // Two positive humps above spot (round-number call strike clusters).
      const lowHump = Math.exp(-Math.pow((x - 0.25) / 0.15, 2)) * 0.7;
      const highHump = Math.exp(-Math.pow((x - 0.65) / 0.18, 2)) * 0.95;
      const tail = x < -0.2 ? -0.25 * Math.exp(-Math.pow((x + 0.5) / 0.2, 2)) : 0;
      return lowHump + highHump + tail + noise * 0.2;
    }
    case "balanced": {
      // Symmetric mild positive cluster, small negative wings.
      const peak = Math.exp(-Math.pow(x / 0.3, 2)) * 0.65;
      const wings = -0.18 * (Math.exp(-Math.pow((x - 0.7) / 0.15, 2)) + Math.exp(-Math.pow((x + 0.7) / 0.15, 2)));
      return peak + wings + noise * 0.22;
    }
    case "negative_dominant": {
      // Mostly negative across all strikes — bear regime.
      const trough = -Math.exp(-Math.pow((x + 0.1) / 0.45, 2));
      const upperLip = x > 0.4 ? 0.25 * Math.exp(-Math.pow((x - 0.7) / 0.15, 2)) : 0;
      return trough + upperLip + noise * 0.2;
    }
    case "wing_amplifying": {
      // Small at center, large positive at high call strikes, negative at low put strikes.
      const callWing = Math.exp(-Math.pow((x - 0.55) / 0.18, 2)) * 0.85;
      const putWing = -Math.exp(-Math.pow((x + 0.55) / 0.18, 2)) * 0.7;
      const center = -0.1 * Math.exp(-Math.pow(x / 0.15, 2));
      return callWing + putWing + center + noise * 0.18;
    }
  }
}

function buildGex(profile: TickerProfile): GexProfile {
  const rng = mulberry32(profile.walkSeed * 31 + 0x42);
  const buckets: GexBucket[] = [];
  const half = Math.floor(profile.strikeCount / 2);
  let total = 0;
  let absolute = 0;
  for (let i = 0; i < profile.strikeCount; i++) {
    const offset = i - half;
    // Snap to a meaningful round-number strike where possible.
    let strike = profile.spot + offset * profile.strikeStep;
    if (profile.strikeStep >= 1) strike = Math.round(strike);
    else if (profile.strikeStep >= 0.5) strike = Math.round(strike * 2) / 2;
    else strike = Math.round(strike * 4) / 4;

    const shapeVal = gexShape(profile.shape, offset, half, rng);
    const gex = shapeVal * profile.peakMagnitude;
    const contracts = Math.max(0, Math.round((Math.abs(gex) / 1e6) * (1 + (rng() - 0.5) * 0.4)));
    buckets.push({
      strike,
      gex,
      position: -gex / 1.2,
      contracts,
    });
    total += gex;
    absolute += Math.abs(gex);
  }

  return {
    by_strike: buckets,
    by_strike_dense: buckets,
    by_expiry: {
      "2026-05-02": total * 0.45,
      "2026-05-09": total * 0.25,
      "2026-05-16": total * 0.15,
      "2026-06-20": total * 0.10,
      "2026-09-19": total * 0.05,
    },
    total,
    absolute,
  };
}

function buildLevels(profile: TickerProfile, spot: number, gex: GexProfile): Levels {
  // Find call wall (strongest positive bucket) and put wall (strongest negative).
  let callWallStrike: number | null = null;
  let putWallStrike: number | null = null;
  let maxPos = -Infinity;
  let maxNeg = Infinity;
  for (const b of gex.by_strike) {
    if (b.gex > maxPos) {
      maxPos = b.gex;
      callWallStrike = b.strike;
    }
    if (b.gex < maxNeg) {
      maxNeg = b.gex;
      putWallStrike = b.strike;
    }
  }
  // Snap walls to round-number strikes if they're within a step.
  const gammaFlip = +(spot * (1 + profile.gammaFlipOffsetPct)).toFixed(2);

  return {
    gamma_flip: gammaFlip,
    call_wall: callWallStrike ?? Math.round(spot * 1.009),
    put_wall: putWallStrike ?? Math.round(spot * 0.991),
    vol_trigger: +(spot * 0.996).toFixed(2),
    max_pain: +(spot * 1.0005).toFixed(2),
    hvl_zones: [
      { low: +(spot * 1.005).toFixed(2), high: +(spot * 1.012).toFixed(2), label: "HVL upper" },
    ],
    lvz_zones: [
      { low: +(spot * 0.985).toFixed(2), high: +(spot * 0.99).toFixed(2), label: "LVZ lower" },
    ],
    air_pockets: [
      { low: +(spot * 0.995).toFixed(2), high: +(spot * 1.0).toFixed(2), label: "Pocket" },
    ],
    confidence: {
      gamma_flip: "high",
      call_wall: "high",
      put_wall: "high",
      vol_trigger: "high",
      max_pain: "medium",
    },
  };
}

function buildSnapshot(profile: TickerProfile, spotOverride?: number): LiveSnapshot {
  const spot = spotOverride ?? profile.spot;
  // Critical: keep the profile's strike grid STABLE across snapshots — only
  // the spot moves, the strike anchors do not. Otherwise every WS tick
  // produces a slightly different set of rounded strikes, blowing up the
  // strike-union the heatmap renders against (we'd see 41 → 82 → 123…).
  const gex = buildGex(profile);
  const regime: Regime =
    profile.regime ??
    (gex.total > 0 ? "positive_gamma" : gex.total < 0 ? "negative_gamma" : "neutral");
  return {
    ts_ns: Date.now() * 1_000_000,
    ticker: profile.symbol,
    spot,
    regime,
    change_pct: profile.changePct,
    gex,
    levels: buildLevels(profile, spot, gex),
    position: gex.by_strike.slice(0, 12).map((b) => ({
      strike: b.strike,
      expiry: "2026-05-02",
      net_position: b.position ?? 0,
      oi: (b.contracts ?? 0) * 4,
      confidence: "high",
    })),
    stale: false,
  };
}

const PROFILE_BY: Map<Ticker, TickerProfile> = new Map(TICKER_PROFILES.map((p) => [p.symbol, p]));
const SNAPSHOTS = new Map<Ticker, LiveSnapshot>();
for (const t of TICKER_PROFILES) {
  SNAPSHOTS.set(t.symbol, buildSnapshot(t));
}

// ---------------------------------------------------------------------------
// HIRO-style intraday walk: deterministic per ticker. Used by the dashboard
// tile sparklines so they look like real intraday traces, not white noise.
// ---------------------------------------------------------------------------

/**
 * Generate a realistic-looking intraday GEX-total walk of N points.
 *
 * Uses Brownian-bridge-ish behavior with a baseline drift and short bursts —
 * deterministic per `seed` so re-renders return the same series.
 */
export function buildIntradayWalk(seed: number, n: number, scale: number): number[] {
  const rng = mulberry32(seed);
  const out: number[] = [];
  let value = scale * (0.3 + rng() * 0.4);
  let drift = (rng() - 0.5) * 0.002 * scale;
  for (let i = 0; i < n; i++) {
    // Occasional regime burst — a short-lived directional kick.
    if (rng() < 0.04) {
      drift = (rng() - 0.5) * 0.012 * scale;
    }
    // Stochastic step.
    const step = (rng() - 0.5) * 0.012 * scale + drift;
    value += step;
    // Mean-revert slightly so the walk doesn't drift to ±∞.
    value += (scale * 0.5 - value) * 0.012;
    out.push(value);
  }
  return out;
}

// Pre-compute sparkline series per ticker so they're stable across renders.
const SPARKLINES = new Map<Ticker, number[]>();
for (const t of TICKER_PROFILES) {
  SPARKLINES.set(t.symbol, buildIntradayWalk(t.walkSeed, 60, t.peakMagnitude));
}

/** Public accessor — returns a copy so callers can mutate safely. */
export function mockSparkline(ticker: Ticker): number[] {
  return (SPARKLINES.get(ticker) ?? []).slice();
}

/**
 * Generate a synthetic rolling 30-bucket per-strike history so the dashboard
 * Trinity heatmaps have visible cell evolution from the moment they render
 * (instead of showing one single stretched column). Each bucket is the
 * current snapshot's GEX with a deterministic per-strike-per-bucket wobble
 * scaled by the per-ticker walk.
 */
export function mockHistoryFrames(
  ticker: Ticker,
  buckets = 30,
): Array<{ ts: number; map: Map<number, number> }> {
  const profile = PROFILE_BY.get(ticker);
  const snap = SNAPSHOTS.get(ticker);
  if (!profile || !snap) return [];
  const frames: Array<{ ts: number; map: Map<number, number> }> = [];
  // Walk: deterministic "intraday GEX evolution" — each strike gets a
  // per-bucket multiplier that drifts smoothly so the heatmap shows continuous
  // bands of evolving color.
  const baseTs = Date.now() - buckets * 60_000;
  // The mock's per-strike GEX is roughly stable in real markets within a
  // 1-hour window, but to give the heatmap a SpotGamma-TRACE-style "tile-by-
  // tile" look we add per-(strike, bucket) variation strong enough to be
  // visible AND we simulate spot drifting through the strike grid — so the
  // hot zones move diagonally across the panel as time progresses.
  const rng = mulberry32(profile.walkSeed * 191);
  const baseStrikes = snap.gex.by_strike.slice();
  const strikePhases = baseStrikes.map(() => rng() * Math.PI * 2);
  const strikeAmps = baseStrikes.map(() => 0.4 + rng() * 0.5);
  const N = baseStrikes.length;
  // Spot drift in strike-index units. Positive = the gamma profile shifts UP
  // in strike-space (i.e. the user's spot effectively moved DOWN, dragging
  // the relative shape with it).
  const driftMag = 2.5; // how many strikes the shape can drift over the window
  for (let b = 0; b < buckets; b++) {
    const map = new Map<number, number>();
    const tWalk = b / buckets;
    const envelope = 0.55 + 0.45 * Math.sin(tWalk * Math.PI * 1.4 + profile.walkSeed * 0.3);
    const driftStrikes = Math.sin(tWalk * Math.PI * 1.5 + profile.walkSeed * 0.31) * driftMag;
    for (let s = 0; s < N; s++) {
      // Sample the SHAPE at offset (s - half - drift) — interpolate.
      const sampleIdx = s - driftStrikes;
      const i0 = Math.max(0, Math.min(N - 1, Math.floor(sampleIdx)));
      const i1 = Math.max(0, Math.min(N - 1, Math.ceil(sampleIdx)));
      const frac = sampleIdx - Math.floor(sampleIdx);
      const baseGex = baseStrikes[i0].gex * (1 - frac) + baseStrikes[i1].gex * frac;
      // Per-(strike, bucket) wobble — keeps sign of base, multiplies magnitude.
      const wobble =
        1 +
        strikeAmps[s] * Math.sin(tWalk * Math.PI * 2.6 + strikePhases[s]) +
        0.18 * Math.sin(tWalk * Math.PI * 5.3 + s * 0.71);
      map.set(baseStrikes[s].strike, baseGex * envelope * wobble);
    }
    frames.push({ ts: baseTs + b * 60_000, map });
  }
  return frames;
}

function jitter(snapshot: LiveSnapshot): LiveSnapshot {
  const profile = PROFILE_BY.get(snapshot.ticker);
  if (!profile) return snapshot;
  // Wobble spot ±0.05% so the live-tick pulse has visible motion but doesn't
  // visually stampede the heatmap.
  const wobble = 1 + (Math.random() - 0.5) * 0.0005;
  const newSpot = snapshot.spot * wobble;
  const next = buildSnapshot(profile, newSpot);
  next.ts_ns = Date.now() * 1_000_000;
  // Append the latest total to the sparkline so it evolves visibly.
  const arr = SPARKLINES.get(snapshot.ticker) ?? [];
  arr.push(next.gex.total);
  if (arr.length > 60) arr.shift();
  SPARKLINES.set(snapshot.ticker, arr);
  return next;
}

// ---------------------------------------------------------------------------
// Whitepaper — rendered on the Methodology page. Loaded lazily via fetch on
// the Vite dev server so changes to `docs/whitepaper/v1.md` show up live.
// ---------------------------------------------------------------------------

const WHITEPAPER_FALLBACK: MethodologyWhitepaper = {
  version: "1.0.0",
  published_at: "2026-05-02",
  sha256: "embedded-fallback",
  // Embedded whitepaper text — kept synced with `docs/whitepaper/v1.md` (~2k
  // words, KaTeX-ready). Keeping the markdown in-bundle avoids a fetch round
  // trip and ensures the methodology page always renders even offline.
  markdown: `# DealerPulse Methodology Whitepaper

**Version:** 1.0.0
**Published:** 2026-05-02
**Authors:** DealerPulse Team
**Audience:** Active options traders, quants, and anyone who wants to know exactly what we are computing and why.

---

## Why This Document Exists

Most published gamma-exposure dashboards do not show their math. We do. If you are paying for analytics that move money, you deserve to know what assumptions sit behind every number on the screen.

This whitepaper covers:

1. What gamma exposure (GEX) is and why it moves price
2. How DealerPulse infers dealer positioning, including the configurable sign convention
3. How we handle 0DTE — the part of the market that breaks naive GEX models
4. How we fit the implied-volatility surface
5. Known limitations of the model — the parts where we are estimating, not measuring

References to source literature are footnoted throughout.

---

## 1. Gamma Exposure (GEX)

When you buy or sell an option, a market maker takes the other side. To run a balanced book, the market maker continuously hedges the directional risk of that option position by trading the underlying. The amount of underlying they must trade per unit move in spot is called *gamma exposure*.

GEX is *the dollar amount of underlying that dealers must buy or sell per 1% move in price* if you assume dealers hold the inverse of customer positioning at each strike.

### 1.1 Per-strike formula

For a single contract at strike $K$ with open interest $\\text{OI}$, on an underlying with spot $S$:

$$ \\text{GEX}(K) = \\Gamma(K) \\cdot \\text{OI}(K) \\cdot 100 \\cdot S^2 \\cdot 0.01 $$

Where $\\Gamma(K)$ is the Black-Scholes (or Bjerksund-Stensland 2002 for American options) gamma at strike $K$ under the current implied vol surface. The constants:

- $100$ is the equity options contract multiplier
- $S^2$ converts per-share gamma into dollar-gamma (a 1% spot move equals an $S \\cdot 0.01$ price move; the second derivative scales with the square)
- $0.01$ scales the result to "per-1%-move" units rather than per-1.00-move

For a chain, we sum across calls and puts separately (with their respective sign conventions; see §2):

$$ \\text{GEX}_{\\text{total}} = \\sum_K \\left( \\Gamma_c(K) \\cdot D_c(K) + \\Gamma_p(K) \\cdot D_p(K) \\right) \\cdot 100 \\cdot S^2 \\cdot 0.01 $$

### 1.2 The two regimes

When **GEX > 0** (dealers net long gamma): dealers sell into rallies and buy into dips. This **suppresses volatility**. Tape reads as range-bound, mean-reverting.

When **GEX < 0** (dealers net short gamma): dealers buy into rallies and sell into dips. This **amplifies volatility**. Tape reads as trending, with cascading moves.

The *gamma flip level* is the spot price at which $\\text{GEX}_{\\text{total}} = 0$. Crossing it is widely watched as a regime change.

### 1.3 Source

Kochuba, B. (2017). *Gamma Exposure*. Squeezemetrics whitepaper.

---

## 2. Dealer Positioning Inference

Open interest tells you how many contracts are outstanding at a strike. It does not tell you which side dealers hold. Every GEX model has to make an assumption — DealerPulse makes that assumption configurable.

### 2.1 Default conventions per ticker class

| Class | Default | Rationale |
|---|---|---|
| Indices (SPX, NDX, VIX) | naive — short calls, long puts | Structural retail/institutional preference: traders buy calls for upside, buy puts for protection. |
| ETFs (SPY, QQQ, IWM) | naive — short calls, long puts | Same as indices for the same reason. |
| Single names (NVDA, AAPL) | trade-classified | Single names have heterogeneous flows; the naive convention breaks down. |

### 2.2 The four conventions

| Name | Description |
|---|---|
| naive_short_calls_long_puts | Dealers hold $-\\text{OI}_c$ calls and $+\\text{OI}_p$ puts. Squeezemetrics default. |
| naive_short_all | Dealers hold $-\\text{OI}_c$ calls and $-\\text{OI}_p$ puts. |
| trade_classified | Dealers hold the negative of inferred customer position via Lee-Ready (§2.3). |
| hybrid_70_30 | Weighted blend: 70% trade-classified + 30% naive. |

Users can override the default per ticker in Settings. Every metric we publish is tagged with the convention used at the time of computation.

### 2.3 Lee-Ready trade-side classification

For each trade, we infer whether it was buy- or sell-initiated:

1. **Quote rule:** if the trade prints above the bid-ask midpoint, it is a buy; below, a sell.
2. **Tick rule (fallback):** if the trade prints exactly at the midpoint, compare to the prior trade — uptick = buy, downtick = sell.
3. **Ambiguous:** if neither rule resolves, classify as AMBIG and track separately as a confidence band.

Reference: Lee, C. and Ready, M. (1991). *Inferring Trade Direction from Intraday Data*. Journal of Finance.

We then accumulate dealer position as:

$$ D(K, \\text{cp}) = -\\left[ \\text{pos}_{\\text{open}}(K, \\text{cp}) + \\sum_t (\\text{buys} - \\text{sells}) \\right] $$

Where $\\text{pos}_{\\text{open}}$ is yesterday's OCC open interest at that strike (zero for 0DTE — see §3).

### 2.4 EOD reconciliation

Every morning after OCC publishes new open interest, we compare our end-of-day inferred position to the actual OI delta. The error is our model bias — tracked per ticker over a rolling 30-day window. If a ticker shows persistent bias greater than ±5%, we flag it for methodology review.

---

## 3. Why 0DTE Breaks Naive Models

About half of SPX options volume in 2026 is in 0DTE contracts — options that open and close on the same trading day. OCC publishes open interest end-of-day only, so 0DTE positions never appear in OI data. Naive GEX models that read OI as their position baseline are blind to half the SPX gamma book.

### 3.1 Our approach

For 0DTE expiries, we set $\\text{pos}_{\\text{open}}(K, \\text{cp}) = 0$ and rely entirely on our intraday Lee-Ready estimator from §2.3. This means:

- 0DTE position estimates start at zero each morning
- They build up as trades flow in
- They reset overnight

By 10:30 AM ET, the 0DTE book typically has 2-4 hours of accumulated trade flow.

### 3.2 Charm dominance

For 0DTE contracts in their last hours, *charm* — the rate at which delta decays toward 0 (OTM) or ±1 (ITM) — becomes the dominant Greek by magnitude. Dealers short OTM 0DTE calls into expiry have to buy back hedges as charm collapses their delta exposure. This is the well-documented "afternoon rally" pattern on SPX OPEX days.

Our charm-flow estimate computes the dollar-delta hedging demand over the remaining session, in 5-minute buckets, with time-to-expiry re-evaluated at each step.

---

## 4. The Implied Vol Surface

Greeks require an implied volatility per strike per expiry. The market quotes prices, not vols, so we have to back-solve.

### 4.1 IV solver

For each contract with a fresh quote, we compute the IV that makes the Black-Scholes (or B-S 2002 for American) price equal to the bid-ask midpoint:

1. Newton-Raphson on $\\text{BS}_{\\text{price}}(\\sigma) - m = 0$
2. Initial guess: $\\sigma = 0.20$
3. Step: $\\sigma \\leftarrow \\sigma - (\\text{price}(\\sigma) - m) / \\text{vega}(\\sigma)$
4. Convergence: $|residual| < 10^{-6}$ or 50 iterations
5. Fallback: bisection on $[10^{-4}, 5.0]$ if Newton diverges or vega → 0

### 4.2 SVI surface fit

Per-strike IVs are noisy, especially in the wings. We fit a smooth parametric surface using the Stochastic Volatility Inspired (SVI) model.

For a single expiry, total implied variance $w(k)$ as a function of log-moneyness $k = \\ln(K / F)$:

$$ w(k) = a + b \\left( \\rho (k - m) + \\sqrt{(k - m)^2 + \\sigma^2} \\right) $$

Five parameters per expiry: $a, b, \\rho, m, \\sigma$. Constraints (no-arbitrage on the slice):

- $b \\ge 0$
- $|\\rho| \\le 1$
- $\\sigma > 0$
- $a + b\\sigma\\sqrt{1 - \\rho^2} \\ge 0$

We fit with weighted least squares (downweighting wide bid-ask spreads). Refit every 5 minutes per expiry.

Reference: Gatheral, J. (2004). *A Parsimonious Arbitrage-Free Implied Volatility Parameterization*. Bachelier Congress.

---

## 5. Known Limitations

Honest list:

1. **Dealer position is inferred, not observed.** Even with trade classification, we cannot see actual dealer inventory.
2. **Complex spreads aren't decomposed.** A customer's risk-reversal looks like one short put + one long call in OI; our model treats each leg independently.
3. **Off-exchange flow is invisible.** Roughly 30-40% of equity volume happens in dark pools and OTC.
4. **Single-name accuracy varies.** Tickers with heavy structured-product flow (covered calls, dispersion trades) systematically violate the naive convention.
5. **No vendor confirms our Greeks.** We compute Black-Scholes (European) and Bjerksund-Stensland 2002 (American) ourselves.
6. **No machine-learning corrections.** The model is fully transparent and deterministic — adding ML layers would compromise transparency without obvious accuracy gains at this scale.

---

## 6. References

- Black, F. and Scholes, M. (1973). *The Pricing of Options and Corporate Liabilities*. Journal of Political Economy.
- Bjerksund, P. and Stensland, G. (2002). *Closed Form Valuation of American Options*.
- Gatheral, J. (2004). *A Parsimonious Arbitrage-Free Implied Volatility Parameterization*.
- Hull, J. (2021). *Options, Futures, and Other Derivatives* (11th ed.). Pearson.
- Kochuba, B. (2017). *Gamma Exposure*. Squeezemetrics whitepaper.
- Lee, C. and Ready, M. (1991). *Inferring Trade Direction from Intraday Data*. Journal of Finance.

---

## 7. Disclaimer

DealerPulse is research and analytics. It is not investment advice. The model assumes the publisher's exemption under the Investment Advisers Act of 1940 (Lowe v. SEC, 472 U.S. 181 (1985)) — outputs are general-circulation, impersonal, and bona-fide. Nothing here is tailored to your situation. You may lose money trading options. Past performance does not predict future results.
`,
};

let _whitepaperPromise: Promise<MethodologyWhitepaper> | null = null;

async function loadWhitepaper(): Promise<MethodologyWhitepaper> {
  // In dev, try to fetch the live markdown so authoring is hot-reloaded.
  if (typeof fetch === "function") {
    try {
      const res = await fetch("/whitepaper.md");
      if (res.ok) {
        const md = await res.text();
        if (md.startsWith("#")) {
          return { ...WHITEPAPER_FALLBACK, markdown: md };
        }
      }
    } catch {
      /* fall through to fallback */
    }
  }
  return WHITEPAPER_FALLBACK;
}

function getWhitepaper(): Promise<MethodologyWhitepaper> {
  if (!_whitepaperPromise) _whitepaperPromise = loadWhitepaper();
  return _whitepaperPromise;
}

// ---------------------------------------------------------------------------
// Priority Alerts mock data (Plan 10). Hand-crafted to match the dashboard
// mockup contract so the UI looks fully wired before the production
// /api/v1/alerts/priority endpoint is built. SPY HIGH 82, QQQ HIGH 76, IWM
// MEDIUM 46 wedge-only, NVDA MEDIUM 55 GEX-only.
// ---------------------------------------------------------------------------

const PRIORITY_ALERTS: PriorityAlert[] = [
  {
    id: "SPY-W-2026.05.02-1431",
    ticker: "SPY",
    priority: "high",
    score: 82,
    scoreLabel: "High",
    sources: { wedge: 0.9, gex: 0.8, flow: 0.15, off: { flow: true } },
    firedAt: "2026-05-02T18:31:48Z",
    firedAtDisplay: "14:31:48 ET · 19s ago",
    scannerMode: "a_plus",
    thesis: {
      symbol: "SPY",
      action: { word: "LONG", tone: "long" },
      segments: [
        { type: "text", text: " · ascending wedge " },
        { type: "text", text: "— " },
        { type: "bold", text: "A+" },
        { type: "text", text: " confirm + GEX call wall " },
        { type: "bold", text: "thickening above" },
        { type: "text", text: "; dealer hedging supports continuation." },
      ],
    },
    tags: [
      { kind: "wedge", icon: "▲", label: "wedge:", text: "ascending · A+" },
      { kind: "gex", icon: "●", label: "gex:", text: "call wall +12k · long-γ regime" },
    ],
    outcomes: {
      label: "last 14 analogs:",
      ticks: ["win","win","loss","win","win","flat","win","win","loss","win","win","win","loss","win"],
      summary: "10 wins · 3 losses · 1 flat",
    },
    tradeCells: [
      { label: "spot", value: "582.31" },
      { label: "break", value: "582.18", tone: "entry" },
      { label: "γ flip", value: "580.90" },
      { label: "wall", value: "585.00", tone: "target" },
      { label: "win rate", value: "71% (14 setups)", tone: "wr" },
    ],
    chart: {
      pattern: "spy_wedge",
      defaultTimeframe: "5m",
      // Color discipline: γ-flip = gold (king-glow), walls = info blue
      // (gatekeeper) for call / dimmed-red for put. No purple on dashboard.
      priceLines: [
        { price: 580.90, color: "var(--accent-gold)", title: "γ flip" },
        { price: 585.00, color: "var(--node-gatekeeper)", title: "call wall" },
        { price: 578.00, color: "var(--color-bearish)", title: "put wall" },
      ],
      // Dashboard cards: candles + price lines + auto-derived wedge + native
      // breakout marker. Orb constellation lives on the per-ticker detail
      // view (/tickers/SPY) where it has the canvas to communicate magnitude.
      overlays: {
        wedge: { swingCutoff: 0.82, lookback: 2 },
      },
    },
    footerLeft: [
      { label: "signal id", mono: "SPY-W-2026-05-02-1431" },
      { label: "scanner mode", mono: "A+" },
    ],
  },
  {
    id: "QQQ-W-2026.05.02-1429",
    ticker: "QQQ",
    priority: "high",
    score: 76,
    scoreLabel: "High",
    sources: { wedge: 0.75, gex: 0.85, flow: 0.20, off: { flow: true } },
    firedAt: "2026-05-02T18:29:12Z",
    firedAtDisplay: "14:29:12 ET · 2m ago",
    scannerMode: "standard",
    thesis: {
      symbol: "QQQ",
      action: { word: "LONG", tone: "long" },
      segments: [
        { type: "text", text: " · falling wedge " },
        { type: "text", text: "— " },
        { type: "bold", text: "standard" },
        { type: "text", text: " mode + put wall " },
        { type: "bold", text: "thinning below" },
        { type: "text", text: " while call wall building above." },
      ],
    },
    tags: [
      { kind: "wedge", icon: "▼", label: "wedge:", text: "falling · standard" },
      { kind: "gex", icon: "●", label: "gex:", text: "put wall −4k · regime flip up" },
    ],
    outcomes: {
      label: "last 22 analogs:",
      ticks: ["win","win","loss","win","win","win","flat","loss","win","win","loss","win","win","flat","win","loss","win","win","loss","win","win","loss"],
      summary: "15 wins · 6 losses · 2 flat",
    },
    tradeCells: [
      { label: "spot", value: "502.54" },
      { label: "break", value: "502.40", tone: "entry" },
      { label: "γ flip", value: "501.70" },
      { label: "wall", value: "505.00", tone: "target" },
      { label: "win rate", value: "68% (22 setups)", tone: "wr" },
    ],
    chart: {
      pattern: "qqq_wedge",
      defaultTimeframe: "5m",
      priceLines: [
        { price: 501.70, color: "var(--accent-gold)", title: "γ flip" },
        { price: 505.00, color: "var(--node-gatekeeper)", title: "call wall" },
        { price: 500.30, color: "var(--color-bearish)", title: "put wall" },
      ],
      overlays: {
        wedge: { swingCutoff: 0.82, lookback: 2 },
      },
    },
    footerLeft: [
      { label: "signal id", mono: "QQQ-W-2026-05-02-1429" },
      { label: "scanner mode", mono: "standard" },
    ],
  },
  {
    id: "IWM-W-2026.05.02-1425",
    ticker: "IWM",
    priority: "medium",
    score: 46,
    scoreLabel: "Medium",
    sources: { wedge: 0.65, gex: 0.25, flow: 0.10, off: { gex: true, flow: true } },
    firedAt: "2026-05-02T18:25:00Z",
    firedAtDisplay: "14:25:00 ET · 7m ago",
    scannerMode: "standard",
    scannerModeDisplay: "standard (pre-break)",
    thesis: {
      symbol: "IWM",
      segments: [
        { type: "text", text: " · symmetric wedge tightening at apex — no break yet, no GEX tailwind. Watch for breakout candle." },
      ],
    },
    tags: [
      { kind: "wedge", icon: "◆", label: "wedge:", text: "symmetric · 3 touches" },
      { kind: "neutral", icon: "●", label: "gex:", text: "mixed regime · no edge" },
    ],
    outcomes: {
      label: "last 18 wedge-only:",
      ticks: ["loss","win","loss","win","flat","loss","win","loss","win","loss","flat","win","loss","win","win","loss","loss","flat"],
      summary: "7 wins · 8 losses · 3 flat · 47% win rate",
    },
    tradeCells: [
      { label: "spot", value: "230.01" },
      { label: "apex", value: "230.30" },
      { label: "γ flip", value: "232.00" },
      { label: "status", value: "forming" },
    ],
    chart: {
      pattern: "iwm_wedge",
      defaultTimeframe: "5m",
      priceLines: [
        { price: 232.00, color: "var(--accent-gold)", title: "γ flip" },
        { price: 228.50, color: "var(--color-bearish)", title: "put wall" },
      ],
      overlays: {
        wedge: { swingCutoff: 1.0, lookback: 2 },  // forming wedge — sample full range
        apexLabel: { frac: 0.92, y: 0.52 },
      },
    },
    footerLeft: [{ label: "scanner mode", mono: "standard (pre-break)" }],
  },
  {
    id: "NVDA-G-2026.05.02-1418",
    ticker: "NVDA",
    priority: "medium",
    score: 55,
    scoreLabel: "Medium",
    sources: { wedge: 0.05, gex: 0.70, flow: 0.30, off: { wedge: true, flow: true } },
    firedAt: "2026-05-02T18:18:33Z",
    firedAtDisplay: "14:18:33 ET · 14m ago",
    scannerMode: "standard",
    thesis: {
      symbol: "NVDA",
      segments: [
        { type: "text", text: " · spot drifting toward γ-flip " },
        { type: "bold", text: "138.00" },
        { type: "text", text: " — " },
      ],
      action: { word: "SHORT-γ unlock", tone: "short" },
    },
    tags: [
      { kind: "gex", icon: "●", label: "gex:", text: "γ-flip −0.5pt · regime gate" },
      { kind: "neutral", icon: "▲", label: "wedge:", text: "none active" },
    ],
    outcomes: {
      label: "last 31 γ-flip crosses:",
      ticks: ["loss","win","flat","loss","win","loss","win","loss","loss","win","flat","win","loss","win","flat","loss","win","loss","loss","win","flat","win","loss","win","loss","win","loss","win","loss","loss","win"],
      summary: "14 wins · 13 losses · 4 flat · 52% win rate",
    },
    tradeCells: [
      { label: "spot", value: "138.50" },
      { label: "γ flip", value: "138.00" },
      { label: "below", value: "137.20", tone: "stop" },
      { label: "status", value: "approaching" },
    ],
    chart: {
      pattern: "nvda_drift",
      defaultTimeframe: "5m",
      priceLines: [
        { price: 138.00, color: "var(--accent-gold)", title: "γ flip" },
        { price: 137.20, color: "var(--color-bearish)", title: "below" },
        { price: 140.50, color: "var(--node-gatekeeper)", title: "call wall" },
      ],
      // No wedge, no orbs — this is a pure-GEX drift setup. The price lines
      // alone communicate the levels; the thesis text and tags do the rest.
      overlays: {},
    },
    footerLeft: [{ text: "watching · spot vs γ-flip · cross fires alert" }],
  },
];

const PRIORITY_ALERTS_RESPONSE: PriorityAlertsResponse = {
  alerts: PRIORITY_ALERTS,
  scanner: {
    highCount: 2,
    singleSourceCount: 3,
    lastScanDisplay: "2s ago",
    activeModes: ["a_plus", "standard"],
  },
  emptyState: {
    title: "Scanner watching · nothing meets bar",
    sub: "13 tickers · wedge scanner in A+ + standard · GEX regime + flow gates active. A signal fires here when ≥ 2 source types align.",
    statuses: [
      { ticker: "SPX",  symbol: "SPX ✓",  status: "ok" },
      { ticker: "NDX",  symbol: "NDX 👁",  status: "watch" },
      { ticker: "VIX",  symbol: "VIX ⏸",  status: "off" },
      { ticker: "SPY",  symbol: "SPY ✓",  status: "ok" },
      { ticker: "QQQ",  symbol: "QQQ 👁",  status: "watch" },
      { ticker: "IWM",  symbol: "IWM 👁",  status: "watch" },
      { ticker: "AAPL", symbol: "AAPL ✓", status: "ok" },
      { ticker: "META", symbol: "+ 6", status: "off" },
    ],
  },
};

export interface MockApi {
  listTickers(): Promise<{ tickers: Ticker[] }>;
  getSnapshot(ticker: Ticker): Promise<LiveSnapshot>;
  getLevels(ticker: Ticker): Promise<Levels>;
  getGex(ticker: Ticker): Promise<GexProfile>;
  getHistory(ticker: Ticker, from: string, to: string): Promise<HistoryResponse>;
  getMethodology(ticker: Ticker): Promise<Methodology>;
  getWhitepaper(): Promise<MethodologyWhitepaper>;
  getMe(): Promise<UserProfile>;
  updateSettings(settings: Record<string, unknown>): Promise<UserProfile>;
  getPublicSpxGex(): Promise<LiveSnapshot>;
  getPriorityAlerts(): Promise<PriorityAlertsResponse>;
  getRelayAlerts(params?: { ticker?: string; type?: string; limit?: number }): Promise<RelayAlertsResponse>;
}

let _mock: MockApi | null = null;

export function getMockApi(): MockApi {
  if (_mock) return _mock;
  _mock = {
    async listTickers() {
      return { tickers: TICKER_PROFILES.map((t) => t.symbol) };
    },
    async getSnapshot(ticker) {
      const snap = SNAPSHOTS.get(ticker);
      if (!snap) throw new Error(`mock: unknown ticker ${ticker}`);
      return jitter(snap);
    },
    async getLevels(ticker) {
      const snap = SNAPSHOTS.get(ticker);
      if (!snap) throw new Error(`mock: unknown ticker ${ticker}`);
      return snap.levels;
    },
    async getGex(ticker) {
      const snap = SNAPSHOTS.get(ticker);
      if (!snap) throw new Error(`mock: unknown ticker ${ticker}`);
      return snap.gex;
    },
    async getHistory(ticker, from, to) {
      const snap = SNAPSHOTS.get(ticker);
      const profile = PROFILE_BY.get(ticker);
      const spot = snap?.spot ?? 100;
      // Build a deterministic spot walk from the per-ticker seed so the chart
      // matches the sparkline visually.
      const walk = buildIntradayWalk((profile?.walkSeed ?? 1) * 7, 90, 1);
      const points = walk.map((w, i) => ({
        ts: Date.now() - (90 - i) * 60_000,
        spot: spot * (1 + (w / Math.max(1, ...walk.map(Math.abs))) * 0.005),
        total_gex: snap?.gex.total ?? 0,
        gamma_flip: snap?.levels.gamma_flip ?? null,
      }));
      return { ticker, from, to, points };
    },
    async getMethodology(ticker) {
      const snap = SNAPSHOTS.get(ticker);
      return {
        ticker,
        sign_convention: "naive_short_calls",
        version: "1.0.0",
        formulas: {
          gex: "GEX(K) = \\Gamma(K) \\cdot OI(K) \\cdot 100 \\cdot S^2 \\cdot 0.01",
          gamma_flip: "\\sum_K GEX(K, S^*) = 0",
        },
        inputs: {
          spot: snap?.spot ?? 0,
          total_gex: snap?.gex.total ?? 0,
          absolute: snap?.gex.absolute ?? 0,
          convention: "naive_short_calls",
        },
      };
    },
    async getWhitepaper() {
      return getWhitepaper();
    },
    async getMe() {
      return {
        user_id: "mock-user",
        email: "demo@dealerpulse.local",
        plan: "pro",
        allowed_tickers: TICKER_PROFILES.map((t) => t.symbol),
        features: ["realtime", "methodology_overrides", "api_access"],
        settings: {},
      };
    },
    async updateSettings(settings) {
      const me = await this.getMe();
      return { ...me, settings };
    },
    async getPublicSpxGex() {
      const snap = SNAPSHOTS.get("SPX")!;
      return jitter(snap);
    },
    async getPriorityAlerts() {
      return PRIORITY_ALERTS_RESPONSE;
    },
    async getRelayAlerts(params: { ticker?: string; type?: string; limit?: number } = {}) {
      // Synthetic — three sample alerts for the most common tickers so the
      // panel has content during dev. Real data flows in via the live API
      // when VITE_USE_MOCK=false.
      const all: RelayAlertsResponse = {
        alerts: [
          {
            type: "ai_signal",
            ticker: "SPY",
            payload: {
              type: "ai_signal",
              ticker: "SPY",
              signal: "MASTER HIGH",
              direction: "long_call",
              confidence: 0.91,
              price: 583.41,
              call_wall: 585,
              put_wall: 580,
              regime: "neutral",
              reasons: ["above vwap", "tick thrust bull", "OR breakout up"],
              timestamp: new Date(Date.now() - 60_000).toISOString(),
            },
          },
          {
            type: "ai_signal",
            ticker: "QQQ",
            payload: {
              type: "ai_signal",
              ticker: "QQQ",
              signal: "SNIPER HIGH",
              direction: "long_put",
              confidence: 0.74,
              price: 502.18,
              call_wall: 504,
              put_wall: 500,
              regime: "volatile",
              reasons: ["below vwap", "below pdc"],
              timestamp: new Date(Date.now() - 180_000).toISOString(),
            },
          },
          {
            type: "ai_digest",
            ticker: "",
            payload: {
              type: "ai_digest",
              summary: "Dealers running short γ on QQQ; long γ on SPY. Watch for divergence into 11am.",
              timestamp: new Date(Date.now() - 300_000).toISOString(),
            },
          },
        ],
      };
      const limit = params.limit ?? 50;
      return {
        alerts: all.alerts
          .filter((a) => !params.ticker || a.ticker === params.ticker.toUpperCase())
          .filter((a) => !params.type || a.type === params.type)
          .slice(0, limit),
      };
    },
  };
  return _mock;
}

/**
 * Mock WebSocket: fires synthetic snapshot updates every 1s on subscribed
 * channels.
 */
export function getMockWs(): WsClient {
  const handlers = new Map<string, Set<WsHandler>>();
  let timer: number | undefined;
  let state: WsState = "idle";

  const tick = () => {
    for (const [channel, set] of handlers) {
      const ticker = channel.replace(/^live\./, "");
      const snap = SNAPSHOTS.get(ticker);
      if (!snap) continue;
      const next = jitter(snap);
      SNAPSHOTS.set(ticker, next);
      const msg: WsMessage<LiveSnapshot> = {
        channel,
        ts: Date.now(),
        payload: next,
      };
      for (const h of set) h(msg);
    }
  };

  const start = () => {
    if (timer !== undefined) return;
    timer = window.setInterval(tick, 1000);
  };

  const stop = () => {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };

  return {
    get state() {
      return state;
    },
    connect() {
      state = "open";
      start();
    },
    close() {
      state = "closed";
      stop();
    },
    subscribe(channel, handler) {
      let set = handlers.get(channel);
      if (!set) {
        set = new Set();
        handlers.set(channel, set);
      }
      set.add(handler);
      // Fire an immediate snapshot so consumers don't wait 1s.
      const ticker = channel.replace(/^live\./, "");
      const snap = SNAPSHOTS.get(ticker);
      if (snap) {
        handler({ channel, ts: Date.now(), payload: jitter(snap) });
      }
      return () => {
        const s = handlers.get(channel);
        if (!s) return;
        s.delete(handler);
        if (s.size === 0) handlers.delete(channel);
      };
    },
    resubscribe() {
      tick();
    },
  };
}
